import { useContext, useEffect, useState } from 'react';

import {
    Box,
    Button,
    Flex,
    Table,
    Thead,
    Tbody,
    Tr,
} from '@chakra-ui/react';
import { ModalContext } from '../../../contexts/ModalProvider';
import { Controller } from 'react-hook-form';
import { IField } from '../../../interfaces/IField';
import { DefinedValidations } from '../../../interfaces/Validations';
import { PlusIcon } from '../../../icons';
import MinutesObservationTableRow from './MinutesObservationTableRow';
import TableHeader from '../TableHeader';
import useValidate from '../../../hooks/useValidate';
import MinutesObservationTableModal from './minutesObservationTableModal';


interface IMinutesObservationTable extends IField {
    data?: any;
    description?: string;
    variant?: string;
    help?: string;
    styles?: {
        font?: string
    };
    formIndex?: string;
}

const definedValidations: DefinedValidations = {
    notEmpty: (label, validationValue, value) => {
        if (validationValue && !value) {
            return `Cannot be empty`;
        }
    },
};

const MinutesObservationTable = ({ control, name, label, validations = {}, disabled = false, help = '', required, data, styles, formIndex }: IMinutesObservationTable) => {
    const validate = useValidate(label || name, validations || {}, definedValidations);
    const formValue = control._formValues[name];
    const initialRows = (formValue && Array.isArray(formValue) && formValue.length > 0) ? formValue : (data && Array.isArray(data) ? data : []);
    const [dataValues, setdataValues] = useState<any>(initialRows);
    const { modalState, setModalState, setModalScope } = useContext(ModalContext);
    const [currentDataValue, setCurrentDataValue] = useState<any>('');

    useEffect(() => {
        const next = control._formValues[name];
        setdataValues((next && Array.isArray(next) && next.length > 0) ? next : (data && Array.isArray(data) ? data : []));
    }, [control._formValues[name], name, data]);

    // Reset the form after closing
    useEffect(() => {
        if (modalState === 'closed') {
            setCurrentDataValue('');
            setModalScope(undefined);
        }
    }, [modalState, setModalScope]);

    // If modal opened in edit or delete mode, reset the form and set values of edited element
    const openObservationModal = (action: 'add' | 'edit' | 'delete', dataValue?: any) => {
        if (action !== 'add') {
            setCurrentDataValue(dataValue);
        }
        else {
            setCurrentDataValue({ minutes: undefined, secsPerItem: undefined, observation: "" });
        }
        setModalState(action);
        setModalScope(name);
    };


    return (
        <Controller
            name={name}
            control={control}
            rules={{ validate }}
            render={({ field, fieldState }) => {
                const { onChange } = field;
                const { error } = fieldState;

                return (
                    <>
                        <MinutesObservationTableModal
                            modalState={modalState}
                            setModalState={setModalState}
                            dataValues={dataValues}
                            setdataValues={setdataValues}
                            selectedDataValue={currentDataValue}
                            onChange={onChange}
                            formIndex={formIndex}
                            name={name} />
                        <Box w='full' id={name} mt='none' maxW='700px'>
                            {label && (
                                <Flex pt='15px' pb={1} align='center' justify='space-between' mb='none'>
                                    <Box
                                        color={error ? 'textInput.labelFont.error' : styles ? styles?.font : 'textInput.labelFont.normal'}
                                        fontWeight='bold'
                                        fontSize='md'
                                        position='static'
                                        left='none'
                                        zIndex={2}
                                    >
                                        {label}
                                    </Box>
                                </Flex>
                            )}
                            <Table fontSize='smm'>
                                <Thead>
                                    <Tr>
                                        <TableHeader header='Minutes' styles={{ paddingInline: '0px' }} />
                                        <TableHeader header='Secs/Item' styles={{ paddingInline: '0px' }} />
                                        <TableHeader header='Observation' styles={{ paddingInline: '20px' }} />
                                        <TableHeader header='' styles={{ paddingInline: '20px' }} />
                                        <TableHeader header='' styles={{ paddingInline: '0px' }} />
                                    </Tr>
                                </Thead>
                                <Tbody left='-10px'>
                                    {dataValues && dataValues?.map((dataValue, index) =>
                                        <MinutesObservationTableRow
                                            key={index}
                                            observation={dataValue.observation}
                                            minutes={dataValue.minutes} secsPerItem={dataValue.secsPerItem}
                                            onEdit={() => { openObservationModal('edit', { ...dataValue, index: index }) }}
                                            onRemove={() => openObservationModal('delete', { ...dataValue, index: index })}
                                            disabled={disabled}
                                        />
                                    )}
                                </Tbody>
                            </Table>
                            <Box>
                                {!disabled &&
                                    <Button
                                        disabled={disabled}
                                        p={3}
                                        h="32px"
                                        fontSize="ssm"
                                        bg="brePink"
                                        color="white"
                                        mt='05px'
                                        rounded="6px"
                                        leftIcon={<PlusIcon />}
                                        onClick={() => openObservationModal('add')}
                                        _hover={{
                                            background: 'brePink.hover',
                                        }}
                                        _active={{
                                            background: 'brePink.active',
                                        }}
                                    >
                                        Add Row
                                    </Button>
                                }
                            </Box >
                            {error && <Box fontSize='ssm' ml={1} color='textInput.error'>{error.message}</Box>}
                        </Box >
                    </>
                );
            }}
        />
    );
};

export default MinutesObservationTable;

export const minutesObservationTableStyles = {
    minutesObservationTable: {

    }
};